import { EventHandler, updateCSSText } from '@syncfusion/ej2-base';
import { BlockType } from '../../models/enums';
import { findClosestParent, getElementRect } from '../../common/utils/dom';
import { getBlockContentElement, isNonContentEditableBlock } from '../../common/utils/block';
import * as constants from '../../common/constant';
import { events } from '../../common/constant';
export class FloatingIcon {
    /**
     * Creates a new FloatingIcon instance
     *
     * @param {BlockManager} manager The parent BlockManager instance
     */
    constructor(manager) {
        this.parent = manager;
        this.wireGlobalEvents();
    }
    wireGlobalEvents() {
        this.parent.observer.on('floatingIconsCreated', this.handleFloatingIconsCreated, this);
        this.parent.observer.on('addIconClick', this.handleAddIconClick, this);
        this.parent.observer.on('dragIconClick', this.handleDragIconClick, this);
        this.parent.observer.on(events.destroy, this.destroy, this);
    }
    unWireGlobalEvents() {
        this.parent.observer.off('floatingIconsCreated', this.handleFloatingIconsCreated);
        this.parent.observer.off('addIconClick', this.handleAddIconClick);
        this.parent.observer.off('dragIconClick', this.handleDragIconClick);
        this.parent.observer.off(events.destroy, this.destroy);
    }
    handleFloatingIconsCreated() {
        this.floatingIconContainer = document.getElementById(`${this.parent.rootEditorElement.id}_floatingicons`);
        const addIcon = this.floatingIconContainer.querySelector('.e-floating-icon.e-block-add-icon');
        const dragIcon = this.floatingIconContainer.querySelector('.e-floating-icon.e-block-drag-icon');
        EventHandler.add(addIcon, 'click', this.handleAddIconClick, this);
        EventHandler.add(dragIcon, 'click', this.handleDragIconClick, this);
    }
    /**
     * Shows the floating icons
     *
     * @param {HTMLElement} target - The target element to show the floating icons for.
     * @returns {void}
     * @hidden
     */
    showFloatingIcons(target) {
        let blockElement = target;
        this.hideDragIconForEmptyBlock(blockElement);
        const calloutContent = blockElement.closest('.' + constants.CALLOUT_CONTENT_CLS);
        const isToggleBlock = blockElement.classList.contains('e-toggle-block');
        const tableBlock = findClosestParent(target, '.' + constants.TABLE_BLOCK_CLS);
        const quoteBlock = findClosestParent(target, '.' + constants.QUOTE_BLOCK_CLS);
        if ((calloutContent && blockElement === calloutContent.firstElementChild && !tableBlock) ||
            !this.isFullyVisibleInEditor(blockElement) ||
            this.parent.readOnly) {
            // Do not show floating icons for the first child of a callout content block
            this.hideFloatingIcons();
            return;
        }
        updateCSSText(this.floatingIconContainer, 'display: flex;');
        blockElement = (isToggleBlock && !tableBlock)
            ? blockElement.querySelector('.e-toggle-header')
            : (tableBlock
                ? tableBlock
                : (quoteBlock ? quoteBlock : blockElement));
        const editorRect = getElementRect(this.parent.rootEditorElement);
        const blockElementRect = getElementRect(blockElement);
        const styles = window.getComputedStyle(blockElement);
        const floatingIconRect = this.floatingIconContainer.getBoundingClientRect();
        const marginTop = parseFloat(styles.marginTop) || 0;
        const marginLeft = parseFloat(styles.marginLeft) || 0;
        const paddingTop = parseFloat(styles.paddingTop) || 0;
        const paddingLeft = parseFloat(styles.paddingLeft) || 0;
        const editorScrollTop = this.parent.rootEditorElement.scrollTop || 0;
        const baseTopOffset = blockElementRect.top + marginTop - editorRect.top + editorScrollTop;
        const hasHeading = ['h1', 'h2', 'h3', 'h4'].some((tag) => blockElement.querySelector(tag) !== null);
        const topOffset = hasHeading
            ? (baseTopOffset + floatingIconRect.height / 2 + paddingTop)
            : baseTopOffset + paddingTop;
        const adjustedLeft = (blockElementRect.left - marginLeft) + paddingLeft - (floatingIconRect.width + 5) - editorRect.left;
        const cssText = `top: ${topOffset}px; left: ${adjustedLeft}px; pointer-events: auto;`;
        updateCSSText(this.floatingIconContainer, cssText);
    }
    /**
     * Hides the drag icon for empty block
     *
     * @param {HTMLElement} target - The target element to show the floating icons for.
     * @returns {void}
     * @hidden
     */
    hideDragIconForEmptyBlock(target) {
        const dragIcon = this.floatingIconContainer.querySelector('.e-block-drag-icon');
        updateCSSText(dragIcon, 'display: flex;');
        const ignoredTypes = [BlockType.Code, BlockType.Callout, BlockType.Divider, BlockType.CollapsibleHeading,
            BlockType.CollapsibleParagraph, BlockType.Image, BlockType.Table, BlockType.Quote];
        const blockType = target.getAttribute('data-block-type');
        const isIgnoredtype = blockType && ignoredTypes.indexOf(blockType) !== -1;
        const contentElement = getBlockContentElement(target);
        if (!isIgnoredtype && (contentElement && !contentElement.textContent)) {
            updateCSSText(dragIcon, 'display: none;');
        }
    }
    /**
     * Hides the floating icons
     *
     * @returns {void}
     * @hidden
     */
    hideFloatingIcons() {
        if (this.floatingIconContainer) {
            updateCSSText(this.floatingIconContainer, 'display: none;');
        }
    }
    handleDragIconClick(event) {
        if (!this.parent.blockActionMenuSettings.enable) {
            return;
        }
        const dragIcon = this.floatingIconContainer.querySelector('.e-block-drag-icon');
        dragIcon.classList.add('e-drag-icon-selected');
        this.parent.selectionOverlay.selectionOverlayInfo = { element: this.parent.currentHoveredBlock, direction: 'previous' };
        this.parent.lastHighlightedBlockId = this.parent.currentHoveredBlock.id;
        if (this.parent.selectionOverlay) {
            this.parent.selectionOverlay.show(this.parent.currentHoveredBlock.id);
        }
        this.parent.popupRenderer.adjustPopupPositionRelativeToTarget(this.floatingIconContainer, this.parent.blockActionMenuModule.popupObj);
        const popupElement = document.querySelector('#' + this.parent.rootEditorElement.id + constants.BLOCKACTION_POPUP_ID);
        this.parent.blockActionMenuModule.toggleBlockActionPopup(popupElement.classList.contains('e-popup-open'), event);
        if (this.parent.nodeSelection) {
            this.parent.nodeSelection.clearSelection();
        }
    }
    handleAddIconClick() {
        const block = this.parent.currentHoveredBlock;
        if ((this.parent.currentHoveredBlock.innerText.length > 0) || (isNonContentEditableBlock(block.getAttribute('data-block-type')))) {
            this.parent.execCommand({
                command: 'AddBlock',
                state: {
                    targetBlock: this.parent.currentHoveredBlock,
                    preventUpdateAction: true,
                    forceIgnoreTargetUpdate: true
                }
            });
        }
        else {
            this.parent.setFocusAndUIForNewBlock(block);
        }
        if (this.parent.slashCommandModule) {
            this.parent.isPopupOpenedOnAddIconClick = true;
            this.parent.slashCommandModule.showPopup();
        }
    }
    isFullyVisibleInEditor(blockElement) {
        const editorRect = this.parent.rootEditorElement.getBoundingClientRect();
        const blockRect = blockElement.getBoundingClientRect();
        return (blockRect.top >= editorRect.top &&
            blockRect.bottom <= editorRect.bottom);
    }
    destroy() {
        EventHandler.remove(this.floatingIconContainer.firstChild, 'click', this.handleAddIconClick);
        EventHandler.remove(this.floatingIconContainer.lastChild, 'click', this.handleDragIconClick);
        this.unWireGlobalEvents();
        this.floatingIconContainer = null;
    }
}
